import { Mutation } from '@apollo/client/react/components';
import ClearIcon from '@clearkit/icons/glyphs/CloseOrb';
import MoreIcon from '@clearkit/icons/glyphs/Ellipsis';
import ErrorIcon from '@clearkit/icons/glyphs/Error';
import FolderIcon from '@clearkit/icons/glyphs/Folder';
import OpenFolderIcon from '@clearkit/icons/glyphs/FolderOpen';
import {
  CKField,
  CKFieldLeftElement,
  CKFieldRightElement,
} from '@clearkit/react';
import Highlighter from 'react-highlight-words';
import { Link } from 'react-router-dom';

import ConfirmDeleteModal from '~/components/ConfirmDeleteModal';
import SegmentActionsMenu from '~/components/SegmentActionsMenu';
import ToastsContainer from '~/containers/Toasts';
import history from '~/lib/history';
import paths from '~/lib/paths';
import {
  segmentActionLocations,
  trackSegmentDeleted,
} from '~/lib/segmentAnalytics';

import SegmentsMenuItem from '../SegmentsMenuItem';
import CollectionName from './CollectionName';
import destroyCollectionMutation from './destroyCollection.graphql';
import MoreMenu from './MoreMenu';
import SegmentListIcon from './SegmentListIcon';
import styles from './styles.style';

class Collection extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selected: !this.getClosedState(),
      renaming: false,
      menuOpen: false,
      showDeleteModal: false,
    };
  }

  componentDidUpdate = (previousProps, previousState) => {
    if (previousProps.segmentId !== this.props.segmentId) {
      if (
        this.props.segments.map((s) => s.id).indexOf(this.props.segmentId) > -1
      ) {
        setTimeout(() => {
          this.segmentsDiv.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }, 10);
      }
    }
    if (previousState.selected !== this.state.selected) {
      this.setInitialClosedState(!this.state.selected);
    }
  };

  moreIcon = React.createRef();
  renameInput = React.createRef();
  segmentsDiv = React.createRef();

  segmentSelected = () => {
    const { segments, segmentId, addingSegment } = this.props;
    return segments.map((s) => s.id).indexOf(segmentId) > -1 || addingSegment;
  };

  handleDeleteCollectionError = () => {
    ToastsContainer.addError('There was an error deleting this collection');
  };

  handleDeleteCollectionCompleted = () => {
    const { segments = [] } = this.props;

    ToastsContainer.addSuccess('Collection deleted');

    segments.forEach((segment) => {
      trackSegmentDeleted(segment, segmentActionLocations.collectionsMenu);
    });
  };

  closedStateKey = () => `${this.props.collection.id}:closed`;

  getClosedState = () => !!window.localStorage.getItem(this.closedStateKey());

  setInitialClosedState = (isClosed) => {
    if (isClosed) {
      window.localStorage.setItem(this.closedStateKey(), true);
    } else {
      window.localStorage.removeItem(this.closedStateKey());
    }
  };

  select = (event) => {
    event.preventDefault();
    this.setState((prevState) => {
      return { selected: !prevState.selected };
    });
  };

  renderSegment = (segment, index) => {
    const { classes, segmentId, highlightedIndex, type, search } = this.props;

    const selected = segment.id === segmentId;
    const highlighted = index === highlightedIndex;
    const searchWords = search ? search.toString().split(' ') : [];

    const content = (
      <>
        <span
          className={classnames('flex items-center overflow-hidden w-full', {
            'text-gray-600': !selected,
            'text-blue-500': selected,
          })}>
          <SegmentListIcon
            segment={segment}
            selected={selected}
            className={classes.segmentIcon}
          />
          <span className={classnames(classes.segmentLinkLabel, 'flex-grow')}>
            <Highlighter
              searchWords={searchWords}
              autoEscape
              textToHighlight={segment.name}
              highlightTag={({ children }) => (
                <strong className={classes.highlightedTerm}>{children}</strong>
              )}
            />
          </span>
        </span>
        {segment.searchDependencyErrors && (
          <div className={classes.segmentDisabledNotice}>
            <ErrorIcon />
          </div>
        )}
        <SegmentActionsMenu
          buttonComponent={
            <a
              className={classnames(
                classes.segmentMoreMenu,
                'group-hover:opacity-50 text-gray-600',
                {
                  'text-blue-500': selected,
                  selected,
                  highlighted,
                },
              )}
              href="#none">
              <MoreIcon />
            </a>
          }
          onClone={(clone) => {
            ToastsContainer.addSuccess('Audience cloned');
            history.push(paths.segmentEdit(clone.type, clone.id));
          }}
          onDelete={
            selected
              ? () => {
                  history.replace(paths.segments(segment.type));
                }
              : null
          }
          segment={segment}
          type={type}
          location={segmentActionLocations.collectionsMenu}
        />
      </>
    );

    return (
      <SegmentsMenuItem
        className={classnames(
          'pl-6 group',
          classes.ActionMenuItemMargin,
          classes.iconHover,
        )}
        as={Link}
        to={paths.segment(segment.type, segment.id)}
        selected={selected}
        focused={highlighted}>
        {content}
      </SegmentsMenuItem>
    );
  };

  renderTitle = () => {
    const { classes, collection, type } = this.props;
    const { selected } = this.state;
    const segmentSelected = this.segmentSelected();

    const className = classnames(classes.collectionLink, {
      'text-gray-600': true,
    });

    return (
      <>
        <a href="#none" onClick={this.select} className={className}>
          {selected || segmentSelected ? (
            <OpenFolderIcon className={classes.collectionIcon} />
          ) : (
            <FolderIcon className={classes.collectionIcon} />
          )}
          <span className={classes.collectionLinkLabel}>{collection.name}</span>
        </a>
        <MoreMenu
          className={classnames(classes.collectionMoreMenu, {
            'text-gray-600': true,
          })}
          onRename={() => this.setState({ renaming: true })}
          onDelete={() =>
            this.setState({ showMenu: false, showDeleteModal: true })
          }
          type={type}
          collection={collection}
        />
      </>
    );
  };

  renderRenameInput = () => {
    const { classes, collection, type } = this.props;
    const { selected } = this.state;
    const segmentSelected = this.segmentSelected();
    const iconClassName = classnames(classes.renameCollectionIcon);

    return (
      <CKField className={classnames(classes.renameCollectionInput)}>
        <CollectionName
          type={type}
          collection={collection}
          onBlur={() => {
            this.setState({ renaming: false });
          }}
        />
        <CKFieldLeftElement>
          {selected || segmentSelected ? (
            <OpenFolderIcon className={iconClassName} />
          ) : (
            <FolderIcon className={iconClassName} />
          )}
        </CKFieldLeftElement>
        <CKFieldRightElement isInteractive>
          <ClearIcon
            onClick={() => {
              this.setState({ renaming: false });
            }}
            width="16"
            height="16"
            className={classnames(
              'cursor-pointer opacity-40 hover:opacity-70 transition ease-linear duration-200',
            )}
          />
        </CKFieldRightElement>
      </CKField>
    );
  };

  render() {
    const { selected, renaming, showDeleteModal } = this.state;
    const {
      search,
      classes,
      highlightedIndex,
      segments,
      collection,
      canDelete,
      type,
    } = this.props;

    const segmentIsHighlighted = highlightedIndex > -1;
    const segmentSelected = this.segmentSelected();

    const showSegments =
      selected || segmentIsHighlighted || !!search || segmentSelected;

    return (
      <div
        ref={this.segmentsDiv}
        className={classnames(classes.collection, {
          [classes.openCollection]: selected,
        })}>
        <div className={classes.collectionLinkContainer}>
          {renaming ? this.renderRenameInput() : this.renderTitle()}
        </div>
        {showDeleteModal && (
          <Mutation
            mutation={destroyCollectionMutation}
            variables={{
              input: {
                type,
                id: collection.id,
              },
            }}
            onError={this.handleDeleteCollectionError}
            onCompleted={this.handleDeleteCollectionCompleted}
            refetchQueries={['SegmentsMenu', 'SegmentActivity']}>
            {(mutation) => (
              <ConfirmDeleteModal
                associatedItemNames={segments.map((segment) => segment.name)}
                open
                type="collection"
                item={collection}
                canDelete={canDelete}
                deleteItem={mutation}
                onCompleted={() => this.setState({ menuOpen: false })}
                closeModal={() => {
                  this.setState({
                    showDeleteModal: false,
                    menuOpen: false,
                  });
                }}
              />
            )}
          </Mutation>
        )}
        {showSegments && (
          <div className={classes.collectionSegments}>
            {segments.length > 0 ? (
              segments.map((segment, i) => this.renderSegment(segment, i))
            ) : (
              <div className={classes.noSegments}>No audiences</div>
            )}
          </div>
        )}
      </div>
    );
  }
}

Collection.propTypes = {
  segments: PropTypes.array.isRequired,
  segmentId: PropTypes.string,
  collections: PropTypes.array,
  collection: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    segments: PropTypes.array,
    __typename: PropTypes.string.isRequired,
  }),
  classes: PropTypes.object.isRequired,
  type: PropTypes.oneOf(['Company', 'Person']).isRequired,
  search: PropTypes.string.isRequired,
  highlightedIndex: PropTypes.number.isRequired,
  canDelete: PropTypes.bool.isRequired,
  addingSegment: PropTypes.bool,
};

export default withStyles(styles)(Collection);
